import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldErrors, useFormContext } from 'react-hook-form';
import {
  INSTITUTION_NUMBER_VALIDATION,
  PREVIOUSLY_ENTERED,
  requiredText,
  ROUTING_NUMBER_VALIDATION,
  TRANSIT_NUMBER_VALIDATION,
  trimString,
  validateAccountNumber,
} from '#/Scripts/shared/form/validation';
import { normalizeBankAccountTypeId } from '#/Scripts/sign-up/components/SignUp/helpers';
import Button from '#sign-up/components/common/Button/Button';
import { ButtonVariants } from '#sign-up/components/common/Button/buttonTypes';
import { IconButton } from '#sign-up/components/common/Button/IconButton';
import FormTextField from '#sign-up/components/common/FormTextField/FormTextField';
import Select from '#sign-up/components/common/Select/Select';
import { CreditCardForm } from '#sign-up/components/CreditCardForm/CreditCardForm';
import { Modal } from '#sign-up/components/Modal/Modal';
import {
  BankAccountType,
  BankAccountTypeDisplayNames,
  Country,
  enumToDropdownOptions,
  SignupFormValues,
} from '#sign-up/components/SignUp/types';

export const BankInformationForm = () => {
  const form = useFormContext<SignupFormValues>();
  const [isOpen, setIsOpen] = useState(false);

  const isCanada = form.watch('CountryId') === Country.Canada;
  const trustAccountNumber = form.watch('TrustAccountNumber');
  const operationalAccountNumber = form.watch('OperationalAccountNumber');

  const trustAccountRef = useRef<HTMLInputElement | null>(null);
  const operationalAccountRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    trustAccountRef.current ??= document.querySelector<HTMLInputElement>('input[name="TrustAccountNumber"]');
    operationalAccountRef.current ??= document.querySelector<HTMLInputElement>(
      'input[name="OperationalAccountNumber"]'
    );
  }, []);

  const toggleModal = useCallback(() => setIsOpen((prev) => !prev), []);

  const accountTypeOptions = useMemo(
    () => enumToDropdownOptions(BankAccountType, BankAccountTypeDisplayNames) ?? [],
    []
  );

  const handleEditTrustAccount = useCallback(() => {
    form.setValue('TrustAccountNumber', '');
    trustAccountRef.current?.focus();
  }, [form]);

  const handleEditOperationalAccount = useCallback(() => {
    form.setValue('OperationalAccountNumber', '');
    operationalAccountRef.current?.focus();
  }, [form]);

  const handleReadOnlyClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }, []);

  const handleReadOnlyFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.target.blur();
  }, []);

  const handleAccountNumberBlur = useCallback(
    (fieldName: 'TrustAccountNumber' | 'OperationalAccountNumber') => (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      form.setValue(fieldName, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [form]
  );

  const renderEditIcon = useCallback(
    (isReadonly: boolean, onClick: () => void, testId: string) =>
      isReadonly ? (
        <IconButton
          className="edit-icon"
          iconName="edit"
          title="Edit"
          onClick={onClick}
          variant={ButtonVariants.outline}
          data-testid={testId}
          style={{ border: 'none', boxShadow: 'none' }}
        />
      ) : undefined,
    []
  );

  const renderAccountTypeSelect = useCallback(
    (name: 'TrustAccountTypeId' | 'OperationalAccountTypeId') => (
      <Controller
        name={name}
        control={form.control}
        rules={requiredText('Account Type')}
        render={({ field }) => {
          const normalizedValue = normalizeBankAccountTypeId(field.value);

          return (
            <Select
              label="Account Type"
              placeholder="Select..."
              options={accountTypeOptions}
              value={normalizedValue?.toString() ?? ''}
              onChange={(e) => field.onChange(e.target.value ? Number.parseInt(e.target.value, 10) : undefined)}
              onBlur={field.onBlur}
              name={field.name}
              errors={form.formState.errors as FieldErrors}
              className="with-subtext"
            />
          );
        }}
      />
    ),
    [form.control, form.formState.errors, accountTypeOptions, form]
  );

  return (
    <div className="signup-form bank-information">
      <div className="signup-form-header">
        <h1 className="signup-form-section-header">Primary Bank Account Information</h1>
        <p>
          This account will be used for depositing your funds and debiting any returns or chargebacks. If you require a
          separate account for debits, our support team can help you set that up after your application is complete.
        </p>
      </div>

      <div className="signup-bank-details">
        {isCanada ? (
          <>
            <div className="signup-form-row">
              <div className="field wide">{renderAccountTypeSelect('TrustAccountTypeId')}</div>
            </div>
            <div className="signup-form-row">
              <div className="address-fields">
                <div className="field narrow">
                  <FormTextField
                    label="Institution Number"
                    className="with-subtext"
                    maxLength={3}
                    {...form.register('InstitutionNumber', INSTITUTION_NUMBER_VALIDATION)}
                    errors={form.formState.errors.InstitutionNumber}
                  />
                </div>
                <div className="field narrow">
                  <FormTextField
                    label="Transit Number"
                    className="with-subtext"
                    maxLength={5}
                    {...form.register('TransitNumber', TRANSIT_NUMBER_VALIDATION)}
                    errors={form.formState.errors.TransitNumber}
                  />
                </div>
              </div>
              <FormTextField
                label="Account Number"
                className="with-subtext"
                maxLength={200}
                placeholder={trustAccountNumber === PREVIOUSLY_ENTERED ? 'Previously entered' : undefined}
                readOnly={trustAccountNumber === PREVIOUSLY_ENTERED}
                onClick={trustAccountNumber === PREVIOUSLY_ENTERED ? handleReadOnlyClick : undefined}
                onFocus={trustAccountNumber === PREVIOUSLY_ENTERED ? handleReadOnlyFocus : undefined}
                icon={renderEditIcon(
                  trustAccountNumber === PREVIOUSLY_ENTERED,
                  handleEditTrustAccount,
                  'edit-trust-account-number'
                )}
                errors={form.formState.errors.TrustAccountNumber}
                {...form.register('TrustAccountNumber', {
                  validate: validateAccountNumber,
                  ...trimString,
                  onBlur: handleAccountNumberBlur('TrustAccountNumber'),
                })}
              />
            </div>
            <CreditCardForm />
          </>
        ) : (
          <>
            <div className="signup-form-row">
              {renderAccountTypeSelect('TrustAccountTypeId')}
              <FormTextField
                label="Routing Number"
                className="with-subtext"
                {...form.register('TrustRoutingNumber', ROUTING_NUMBER_VALIDATION)}
                errors={form.formState.errors.TrustRoutingNumber}
              />
              <FormTextField
                label="Account Number"
                className="with-subtext"
                maxLength={200}
                placeholder={trustAccountNumber === PREVIOUSLY_ENTERED ? 'Previously entered' : undefined}
                readOnly={trustAccountNumber === PREVIOUSLY_ENTERED}
                onClick={trustAccountNumber === PREVIOUSLY_ENTERED ? handleReadOnlyClick : undefined}
                onFocus={trustAccountNumber === PREVIOUSLY_ENTERED ? handleReadOnlyFocus : undefined}
                icon={renderEditIcon(
                  trustAccountNumber === PREVIOUSLY_ENTERED,
                  handleEditTrustAccount,
                  'edit-trust-account-number'
                )}
                errors={form.formState.errors.TrustAccountNumber}
                {...form.register('TrustAccountNumber', {
                  validate: validateAccountNumber,
                  ...trimString,
                  onBlur: handleAccountNumberBlur('TrustAccountNumber'),
                })}
              />
            </div>
            <div className="signup-form-header">
              <h1 className="signup-form-section-header">Monthly Fee Debit Account Information</h1>
              <p>
                Note: A bank account is required for initial setup. Once your account is active, you will have the
                option to switch your monthly fee payment method to a credit card.
              </p>
            </div>

            <div className="signup-form-row">
              {renderAccountTypeSelect('OperationalAccountTypeId')}
              <FormTextField
                label="Routing Number"
                errors={form.formState.errors.OperationalRoutingNumber}
                className="with-subtext"
                {...form.register('OperationalRoutingNumber', ROUTING_NUMBER_VALIDATION)}
              />
              <FormTextField
                label="Account Number"
                className="with-subtext"
                maxLength={200}
                placeholder={operationalAccountNumber === PREVIOUSLY_ENTERED ? 'Previously entered' : undefined}
                readOnly={operationalAccountNumber === PREVIOUSLY_ENTERED}
                onClick={operationalAccountNumber === PREVIOUSLY_ENTERED ? handleReadOnlyClick : undefined}
                onFocus={operationalAccountNumber === PREVIOUSLY_ENTERED ? handleReadOnlyFocus : undefined}
                icon={renderEditIcon(
                  operationalAccountNumber === PREVIOUSLY_ENTERED,
                  handleEditOperationalAccount,
                  'edit-operational-account-number'
                )}
                {...form.register('OperationalAccountNumber', {
                  validate: validateAccountNumber,
                  ...trimString,
                  onBlur: handleAccountNumberBlur('OperationalAccountNumber'),
                })}
                errors={form.formState.errors.OperationalAccountNumber}
              />
            </div>
          </>
        )}
      </div>

      <div className="account-conditions">
        <Button onClick={toggleModal} variant={ButtonVariants.link}>
          Why does ePayPolicy request separate bank account numbers?
        </Button>
      </div>

      <Modal
        isOpen={isOpen}
        handleClose={toggleModal}
        title="Why does ePayPolicy request separate bank account numbers?"
        footer={<Button onClick={toggleModal}>Close</Button>}
      >
        <p>
          This provides the option to have our system deposit your funds into one account (e.g., a trust account) and
          pull the monthly fee from another account (e.g., an operational account). If you do not require separate
          accounts, you can enter the same account numbers in both sections.
        </p>
      </Modal>
    </div>
  );
};
